// Fenêtre « Nouvelle course / Modifier la course » (transport, lot T1a). En conciergerie, MissionDialog garde sa
// fenêtre d'origine, inchangée. Saisie pensée pour moins d'une minute : bascule Transfert / Mise à disposition,
// donneur d'ordre (création rapide possible), prise en charge et dépose, passagers, passager, payeur, vol ou train,
// véhicule de la flotte et chauffeur libres sur le créneau. Prise en charge et dépose : carnet de lieux (lot T1b-1).
// Demandes spéciales (lot T1b-2) : pastilles à cocher d'un clic (liste des Réglages) ; précision facultative.
// Règle du serveur : un champ absent = inchangé, "" = vidé. La fenêtre envoie toujours tous les champs de la course.
// Secrétaire administrative (lot T5-0) : ni chauffeur ni véhicule (le dispatch les choisit) ; sur une course déjà
// attribuée, seuls le prix et les notes se modifient (le reste est grisé). Le serveur applique les mêmes règles.
// Indisponibilités (lot T5b) : liste des chauffeurs lue sur GET /dispatch/drivers (même règle que le menu « Attribuer ») ;
// les libres, puis un groupe « Indisponibles » (choisissables : « L'attribuer quand même ? » à l'enregistrement) ; le
// chauffeur actuel, s'il est pris par une autre course, est marqué « (occupé) ».
// Prix (lot T2) : classe de véhicule (remplie par le véhicule choisi, choisissable sans véhicule) et bloc « Prix »
// (`RidePrice` : prix proposé, suppléments, TTC / HT / TVA, tarif horaire × durée).
// Facturation (lot T6, étape B2) : téléphone du passager avec indicatif pays (obligatoire dès qu'un numéro est tapé ou
// changé ; une ancienne course renvoie son numéro tel quel) ; course facturée : prix grisé (« Course facturée (F-…) : prix
// verrouillé ») ; course annulée : frais d'annulation ; commission du concierge (pourcentage du TTC ou montant fixe).
import React, { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { useVocab } from "@/lib/vocab";
import { useRights } from "@/lib/rights";
import { RIDE_KINDS, ORDERER_KINDS, PAYERS, rideKindOf, fleetName } from "@/lib/ride";
import { FlightLinks } from "@/components/RideParts";
import RidePrice from "@/components/RidePrice";
import { Abandon, useUnavailableConfirm } from "@/components/UnavailableConfirm";
import { useSettings } from "@/context/SettingsContext";
import { unavShort } from "@/lib/unavailability";
import PlaceInput from "@/components/PlaceInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, Plus, Check } from "lucide-react";
import { fmtDateTime, missionLabel } from "@/lib/helpers";
import { dayIn } from "@/lib/ride";
import { cn } from "@/lib/utils";
import { phoneCountries, isInternational, splitInternational, formatPhone } from "@/lib/phone";

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
// « 2026-10-20T10:00 » + h heures → même format (heure de l'ordinateur).
const plusHours = (local, h) => {
  const d = new Date(local);
  if (!local || isNaN(d) || !(h > 0)) return "";
  return toLocalInput(new Date(d.getTime() + h * 3600000).toISOString());
};
const hoursOf = (v) => parseFloat(String(v ?? "").replace(",", "."));
const TRANSFER_HOURS = 1; // dépose pré-remplie à « prise en charge + 1 h » (modifiable)

const EMPTY = {
  ride_kind: "transfer", client_id: "", location: "", pickup_place_id: "", start: "", dropoff_location: "", dropoff_place_id: "",
  end: "", duration_hours: "",
  passengers: 1, passenger_name: "", passenger_phone: "", payer: "orderer", travel_ref: "", fleet_vehicle_id: "",
  employee_id: "", price_ttc: 0, notes: "", special_requests: [], vehicle_class: "",
  phone_country: "", phone_other: "", cancellation_fee: "", com_mode: "pct", com_value: "", com_beneficiary: "",
};
// Téléphone enregistré → champs de la fenêtre : « +33611223344 » → indicatif « +33 » et « 6 11 22 33 44 » ; un numéro
// d'avant la règle (« 06 11 22 33 44 », sans indicatif) reste tel quel, indicatif vide.
function phoneFields(v) {
  const parts = splitInternational(v);
  if (!parts) return { passenger_phone: v || "", phone_country: "", phone_other: "" };
  const lisible = formatPhone(v).slice(parts.code.length + 1);
  return phoneCountries().some(([c]) => c === parts.code) ? { passenger_phone: lisible, phone_country: parts.code, phone_other: "" }
    : { passenger_phone: lisible, phone_country: "autre", phone_other: parts.code };
}
const QUICK_EMPTY = { open: false, name: "", phone: "", kind: "hotel", saving: false };

export default function RideDialog({ open, onOpenChange, mission, defaultStart, onSaved }) {
  const voc = useVocab();
  const limited = useRights().secretaryLimited; // secrétaire administrative (lot T5-0)
  const locked = limited && !!mission?.employee_id; // course attribuée : prix et notes seulement
  const [clients, setClients] = useState([]);
  const [fleet, setFleet] = useState([]); // toute la flotte (noms des véhicules déjà choisis)
  const [requestList, setRequestList] = useState([]); // demandes spéciales des Réglages [{ key, label, detail_hint }]
  const [detailOpen, setDetailOpen] = useState({}); // demandes dont le champ « précision » est ouvert
  const [places, setPlaces] = useState([]); // carnet de lieux (retirés compris : le lieu d'une ancienne course reste lisible)
  const [freeFleet, setFreeFleet] = useState(null); // véhicules libres sur le créneau ; null = pas encore reçu
  const [available, setAvailable] = useState(null); // chauffeurs libres ; null = pas encore reçu
  const [unavailable, setUnavailable] = useState([]); // chauffeurs indisponibles sans autre course (lot T5b)
  const { confirmDialog, withConfirm } = useUnavailableConfirm();
  const tz = useSettings().settings?.timezone || "Europe/Monaco";
  const [assigned, setAssigned] = useState(null); // chauffeur déjà assigné à la course ouverte ({ id, name })
  const [conflicts, setConflicts] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [endAuto, setEndAuto] = useState(true); // la dépose suit la prise en charge tant qu'on ne l'a pas changée
  const [quick, setQuick] = useState(QUICK_EMPTY);
  const [saving, setSaving] = useState(false);
  const [classes, setClasses] = useState([]); // classes de véhicule des Réglages (lot T2)
  const [priceDetails, setPriceDetails] = useState(undefined); // détail du prix ; undefined = prix pas touché (rien envoyé)
  const [priceKey, setPriceKey] = useState(0); // le bloc « Prix » repart de la course ouverte à chaque ouverture
  const [phoneTouched, setPhoneTouched] = useState(false); // téléphone pas touché : renvoyé tel qu'il est enregistré
  const billed = (mission?.invoiced || []).length > 0; // course facturée (lot T6) : prix verrouillé
  const cancelled = mission?.status === "cancelled";

  useEffect(() => {
    if (!open) return;
    api.get("/clients").then((r) => setClients(r.data)).catch(() => setClients([]));
    api.get("/fleet").then((r) => setFleet(r.data)).catch(() => setFleet([]));
    api.get("/places").then((r) => setPlaces(r.data)).catch(() => setPlaces([]));
    api.get("/settings/full").then((r) => { setRequestList(r.data.special_requests || []); setClasses(r.data.vehicle_classes || []); })
      .catch(() => setRequestList([]));
    setDetailOpen({});
    if (mission) {
      setForm({
        ...EMPTY,
        ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, mission[k] ?? EMPTY[k]])),
        ride_kind: rideKindOf(mission), start: toLocalInput(mission.start), end: toLocalInput(mission.end),
        duration_hours: mission.duration_hours ?? "", passengers: mission.passengers ?? "",
        special_requests: (mission.special_requests || []).map((r) => ({ key: r.key, label: r.label, detail: r.detail || "" })),
        ...phoneFields(mission.passenger_phone),
        cancellation_fee: mission.cancellation_fee ?? "",
        com_mode: mission.commission?.mode || "pct", com_value: mission.commission?.value ?? "",
        com_beneficiary: mission.commission?.beneficiary || "",
      });
      setEndAuto(false);
    } else {
      const start = toLocalInput(defaultStart);
      setForm({ ...EMPTY, start, end: plusHours(start, TRANSFER_HOURS) });
      setEndAuto(true);
    }
    setConflicts([]); setQuick(QUICK_EMPTY); setAvailable(null); setUnavailable([]); setFreeFleet(null); setAssigned(null);
    setPriceDetails(undefined); setPriceKey((k) => k + 1); setPhoneTouched(false);
    let stale = false;
    const eid = mission?.employee_id;
    if (eid) {
      api.get("/users").then((r) => { if (!stale) setAssigned({ id: eid, name: r.data.find((u) => u.id === eid)?.name || "" }); })
        .catch(() => { if (!stale) setAssigned({ id: eid, name: "" }); });
    }
    return () => { stale = true; };
  }, [open, mission, defaultStart]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPhone = (k, v) => { setPhoneTouched(true); set(k, v); };
  const phoneCode = form.phone_country === "autre" ? form.phone_other.trim() : form.phone_country;
  const phoneMissingCode = phoneTouched && form.passenger_phone.trim() && !isInternational(form.passenger_phone) && !phoneCode;
  // Le passager paie : téléphone obligatoire à la création, ou quand on change le payeur ou le téléphone (décision du
  // 2026-11-01, même règle que le serveur : metier.passenger_phone_missing) ; une ancienne course reste modifiable.
  const phoneRequired = form.payer === "passenger" && !form.passenger_phone.trim()
    && (!mission || mission.payer !== "passenger" || !!(mission.passenger_phone || "").trim());
  const addPlace = (p) => setPlaces((list) => [...list, p]);
  const hourly = form.ride_kind === "hourly";
  const validSlot = form.start && form.end && new Date(form.end) > new Date(form.start);

  // Chauffeurs et véhicules libres sur le créneau ; la course modifiée ne rend pas les siens « occupés ».
  useEffect(() => {
    if (!open || !validSlot || limited) return; // la secrétaire ne choisit ni chauffeur ni véhicule
    let stale = false;
    const params = { start: new Date(form.start).toISOString(), end: new Date(form.end).toISOString() };
    if (mission?.id) params.exclude_id = mission.id;
    api.get("/dispatch/drivers", { params })
      .then((r) => {
        if (stale) return;
        setAvailable(r.data.filter((d) => d.free));
        setUnavailable(r.data.filter((d) => !d.free && d.conflicts.length === 0));
      }).catch(() => { if (!stale) { setAvailable([]); setUnavailable([]); } });
    api.get("/fleet/available", { params })
      .then((r) => { if (!stale) setFreeFleet(r.data); }).catch(() => { if (!stale) setFreeFleet([]); });
    return () => { stale = true; };
  }, [open, mission?.id, form.start, form.end, validSlot, limited]);

  // Prise en charge changée : la dépose suit (transfert, tant qu'elle n'a pas été modifiée) ; la fin d'une mise à
  // disposition est toujours recalculée à partir de la durée.
  const onStart = (start) => setForm((f) => {
    if (f.ride_kind === "hourly") return { ...f, start, end: plusHours(start, hoursOf(f.duration_hours)) };
    return { ...f, start, end: endAuto || !f.end ? plusHours(start, TRANSFER_HOURS) : f.end };
  });
  const onDuration = (v) => setForm((f) => ({ ...f, duration_hours: v, end: plusHours(f.start, hoursOf(v)) }));
  const onKind = (kind) => {
    if (kind === form.ride_kind) return;
    if (kind === "transfer") setEndAuto(true); // retour au transfert : dépose = prise en charge + 1 h
    setForm((f) => {
      if (kind === "hourly") {
        const h = hoursOf(f.duration_hours) > 0 ? f.duration_hours : "";
        return { ...f, ride_kind: kind, duration_hours: h, end: plusHours(f.start, hoursOf(h)) };
      }
      return { ...f, ride_kind: kind, end: plusHours(f.start, TRANSFER_HOURS) };
    });
  };

  const assignedMissing = assigned && ![...(available || []), ...unavailable].some((e) => e.id === assigned.id);
  const selFleet = fleet.find((v) => v.id === form.fleet_vehicle_id);
  const fleetMissing = form.fleet_vehicle_id && !(freeFleet || []).some((v) => v.id === form.fleet_vehicle_id);
  const tooMany = selFleet?.seats && Number(form.passengers) > selFleet.seats;

  // Demandes proposées : celles des Réglages, puis celles de la course retirées des Réglages depuis (toujours
  // affichées, et décochables). Une demande gardée par la course garde son libellé.
  const original = (mission?.special_requests || []).filter((r) => r && r.key);
  const requestOptions = [
    ...requestList,
    ...original.filter((r) => !requestList.some((x) => x.key === r.key)).map((r) => ({ key: r.key, label: r.label, detail_hint: "", retired: true })),
  ];
  const checked = (key) => form.special_requests.some((r) => r.key === key);
  const toggleRequest = (opt) => setForm((f) => {
    if (f.special_requests.some((r) => r.key === opt.key)) return { ...f, special_requests: f.special_requests.filter((r) => r.key !== opt.key) };
    const avant = original.find((r) => r.key === opt.key);
    return { ...f, special_requests: [...f.special_requests, { key: opt.key, label: avant?.label || opt.label, detail: "" }] };
  });
  const setDetail = (key, detail) => setForm((f) => ({ ...f, special_requests: f.special_requests.map((r) => (r.key === key ? { ...r, detail } : r)) }));

  const createClient = async () => {
    if (!quick.name.trim()) return;
    setQuick((q) => ({ ...q, saving: true }));
    try {
      const { data } = await api.post("/clients", { name: quick.name.trim(), phone: quick.phone.trim(), orderer_kind: quick.kind });
      setClients((list) => [...list, data]); set("client_id", data.id);
      toast.success("Client créé"); setQuick(QUICK_EMPTY);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setQuick((q) => ({ ...q, saving: false })); }
  };

  const save = async () => {
    if (!form.client_id) { toast.error("Choisissez le donneur d'ordre"); return; }
    if (!form.location.trim()) { toast.error("Indiquez l'adresse de prise en charge"); return; }
    if (!form.start) { toast.error("Indiquez la date et l'heure de prise en charge"); return; }
    if (hourly && !(hoursOf(form.duration_hours) > 0)) { toast.error("Indiquez la durée de la mise à disposition"); return; }
    if (!validSlot) { toast.error("La dépose doit suivre la prise en charge"); return; }
    if (phoneRequired) { toast.error("Le passager paie : indiquez son téléphone."); return; }
    if (phoneMissingCode) { toast.error("Choisissez l'indicatif pays du téléphone du passager."); return; }
    // Téléphone : pas touché → renvoyé tel qu'il est enregistré (le serveur ne le contrôle pas) ; sinon numéro + indicatif.
    const phone = phoneTouched
      ? { passenger_phone: form.passenger_phone.trim(), ...(form.passenger_phone.trim() && !isInternational(form.passenger_phone) ? { passenger_phone_country: phoneCode } : {}) }
      : { passenger_phone: mission?.passenger_phone || "" };
    const comValue = String(form.com_value ?? "").trim().replace(",", ".");
    const payload = {
      type: RIDE_KINDS[form.ride_kind], ride_kind: form.ride_kind, client_id: form.client_id,
      location: form.location.trim(), dropoff_location: form.dropoff_location.trim(),
      pickup_place_id: form.pickup_place_id, dropoff_place_id: form.dropoff_place_id,
      start: new Date(form.start).toISOString(), end: new Date(form.end).toISOString(),
      duration_hours: hourly ? String(form.duration_hours).replace(",", ".") : "",
      passengers: form.passengers === "" ? "" : Number(form.passengers),
      passenger_name: form.passenger_name.trim(), ...phone, payer: form.payer,
      travel_ref: form.travel_ref.trim(), fleet_vehicle_id: form.fleet_vehicle_id, employee_id: form.employee_id,
      // Course facturée : le prix enregistré est renvoyé tel quel, jamais le détail (le serveur refuserait tout changement).
      price_ttc: billed ? (mission.price_ttc || 0) : parseFloat(form.price_ttc) || 0, notes: form.notes, vehicle_class: form.vehicle_class || "",
      ...(priceDetails !== undefined && !billed ? { price_details: priceDetails } : {}), // détail envoyé seulement si l'on a touché au prix
      ...(cancelled && !billed ? { cancellation_fee: String(form.cancellation_fee ?? "").trim().replace(",", ".") } : {}),
      commission: comValue ? { mode: form.com_mode, value: comValue, beneficiary: form.com_beneficiary.trim() } : "",
      special_requests: form.special_requests.map((r) => ({ key: r.key, detail: (r.detail || "").trim() })),
    };
    setSaving(true);
    try {
      // Chauffeur indisponible : 409, puis « L'attribuer quand même ? » (lot T5b).
      await withConfirm((force) => {
        const config = { params: force ? { force_unavailable: true } : {} };
        return mission ? api.patch(`/missions/${mission.id}`, payload, config) : api.post("/missions", payload, config);
      });
      toast.success(mission ? `${voc.Mission} mise à jour` : `${voc.Mission} créée`);
      setConflicts([]); onSaved(); onOpenChange(false);
    } catch (e) {
      if (e instanceof Abandon) return; // « Retour » : la fenêtre reste ouverte, rien n'est enregistré
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.conflicts) { setConflicts(d.conflicts); toast.error(d.message); }
      else toast.error(apiError(d) || e.message);
    } finally { setSaving(false); }
  };

  const grid2 = "grid grid-cols-1 sm:grid-cols-2 gap-3";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {confirmDialog}
      {/* Échap dans un champ de lieu dont la liste est ouverte : seule la liste se ferme (lot T1b-3, PlaceInput). */}
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl"
        onEscapeKeyDown={(e) => { if (e.target?.closest?.('[role="combobox"][aria-expanded="true"]')) e.preventDefault(); }}>
        <DialogHeader><DialogTitle className="font-display text-2xl">{mission ? `Modifier la ${voc.mission}` : `Nouvelle ${voc.mission}`}</DialogTitle></DialogHeader>
        <div className="space-y-4 min-w-0">
          {locked && (
            <p className="text-sm rounded-xl border border-border bg-muted/50 p-3" data-testid="ride-locked-note">
              {`${voc.Mission} déjà attribuée : seuls le prix et les notes se modifient. Pour le reste, voyez avec le dispatch.`}
            </p>
          )}
          {/* Course attribuée et secrétaire : tout ce bloc est grisé (prix et notes restent dessous, modifiables). */}
          <fieldset disabled={locked} className="space-y-4 min-w-0" data-testid="ride-fields">
          <div className="grid grid-cols-2 gap-1 p-1 rounded-full bg-muted" role="radiogroup" aria-label="Type de course">
            {Object.entries(RIDE_KINDS).map(([k, l]) => (
              <button key={k} type="button" role="radio" aria-checked={form.ride_kind === k} onClick={() => onKind(k)} data-testid={`ride-kind-${k}`}
                className={cn("rounded-full py-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:pointer-events-none", form.ride_kind === k ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground")}>{l}</button>
            ))}
          </div>

          <div className="space-y-2 min-w-0">
            <Label>Donneur d'ordre</Label>
            <Select value={form.client_id} onValueChange={(v) => set("client_id", v)} disabled={locked}>
              <SelectTrigger className="min-w-0 [&>span]:truncate [&>span]:block" data-testid="ride-client"><SelectValue placeholder="Hôtel, conciergerie, agence ou client direct" /></SelectTrigger>
              <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.orderer_kind ? ` — ${ORDERER_KINDS[c.orderer_kind]}` : ""}</SelectItem>)}</SelectContent>
            </Select>
            {/* Zone de clic de 28 px de haut, même apparence : marge intérieure compensée par une marge négative (comme au lot 22). */}
            {!quick.open && !locked && (
              <button type="button" onClick={() => setQuick({ ...QUICK_EMPTY, open: true })} className="text-xs text-accent flex items-center gap-1 py-1.5 -my-1.5" data-testid="ride-new-client">
                <Plus className="h-3.5 w-3.5" /> Nouveau client
              </button>
            )}
            {quick.open && (
              <div className="p-3 rounded-xl border border-border space-y-3" data-testid="quick-client">
                <div className={grid2}>
                  <div className="space-y-1.5"><Label>Nom</Label><Input value={quick.name} onChange={(e) => setQuick((q) => ({ ...q, name: e.target.value }))} autoFocus data-testid="quick-client-name" /></div>
                  <div className="space-y-1.5"><Label>Téléphone</Label><Input type="tel" value={quick.phone} onChange={(e) => setQuick((q) => ({ ...q, phone: e.target.value }))} data-testid="quick-client-phone" /></div>
                </div>
                <div className="space-y-1.5"><Label>Type de donneur d'ordre</Label>
                  <Select value={quick.kind} onValueChange={(v) => setQuick((q) => ({ ...q, kind: v }))}>
                    <SelectTrigger data-testid="quick-client-kind"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(ORDERER_KINDS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => setQuick(QUICK_EMPTY)} data-testid="quick-client-cancel">Annuler</Button>
                  <Button size="sm" className="rounded-full" onClick={createClient} disabled={!quick.name.trim() || quick.saving} data-testid="quick-client-save">Créer le client</Button>
                </div>
              </div>
            )}
          </div>

          <div className={grid2}>
            <div className="space-y-2 min-w-0"><Label>Prise en charge</Label>
              <PlaceInput value={form.location} placeId={form.pickup_place_id} places={places} onPlaceCreated={addPlace}
                onChange={({ text, placeId }) => setForm((f) => ({ ...f, location: text, pickup_place_id: placeId }))}
                placeholder="Lieu du carnet ou adresse" testid="ride-location" disabled={locked} /></div>
            <div className="space-y-2 min-w-0"><Label>Date et heure</Label>
              <Input type="datetime-local" value={form.start} onChange={(e) => onStart(e.target.value)} data-testid="ride-start" /></div>
            <div className="space-y-2 min-w-0"><Label>Dépose</Label>
              <PlaceInput value={form.dropoff_location} placeId={form.dropoff_place_id} places={places} onPlaceCreated={addPlace}
                onChange={({ text, placeId }) => setForm((f) => ({ ...f, dropoff_location: text, dropoff_place_id: placeId }))}
                placeholder={hourly ? "Facultatif" : "Lieu du carnet ou adresse"} testid="ride-dropoff" disabled={locked} /></div>
            {hourly
              ? <div className="space-y-2 min-w-0"><Label>Durée (heures)</Label>
                  <Input type="number" min="0.5" step="0.5" value={form.duration_hours} onChange={(e) => onDuration(e.target.value)} data-testid="ride-duration" />
                  <p className="text-xs text-muted-foreground" data-testid="ride-end-computed">{form.end ? `Fin : ${fmtDateTime(new Date(form.end).toISOString())}` : "Indiquez la durée."}</p></div>
              : <div className="space-y-2 min-w-0"><Label>Date et heure de dépose</Label>
                  <Input type="datetime-local" value={form.end} onChange={(e) => { set("end", e.target.value); setEndAuto(false); }} data-testid="ride-end" /></div>}
          </div>

          <div className={grid2}>
            <div className="space-y-2"><Label>Passagers</Label>
              <Input type="number" min="1" max="60" value={form.passengers} onChange={(e) => set("passengers", e.target.value)} data-testid="ride-passengers" />
              {tooMany && <p className="text-xs text-amber-600" data-testid="ride-seats-warning">{`Plus de passagers que de places (${selFleet.seats}).`}</p>}</div>
            <div className="space-y-2"><Label>Payeur</Label>
              <Select value={form.payer} onValueChange={(v) => set("payer", v)} disabled={locked}>
                <SelectTrigger data-testid="ride-payer"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PAYERS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-2 min-w-0"><Label>Passager</Label>
              <Input value={form.passenger_name} onChange={(e) => set("passenger_name", e.target.value)} placeholder="S'il est différent du donneur d'ordre" data-testid="ride-passenger-name" /></div>
            <div className="space-y-2 min-w-0 sm:col-span-2"><Label>Téléphone du passager</Label>
              {/* Indicatif obligatoire dès qu'un numéro est tapé (sans valeur par défaut) ; « 06 … » ou « 6 … » acceptés. */}
              <div className="flex gap-2 min-w-0">
                <Select value={form.phone_country || "none"} onValueChange={(v) => setPhone("phone_country", v === "none" ? "" : v)}>
                  <SelectTrigger className={cn("w-36 shrink-0 [&>span]:truncate [&>span]:block", phoneMissingCode && "border-destructive")} data-testid="ride-phone-country"><SelectValue placeholder="Indicatif" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Indicatif</SelectItem>
                    {phoneCountries().map(([c, pays]) => <SelectItem key={c} value={c}>{`${c} ${pays}`}</SelectItem>)}
                    <SelectItem value="autre">Autre indicatif…</SelectItem>
                  </SelectContent>
                </Select>
                {form.phone_country === "autre" && <Input className="w-20 shrink-0" value={form.phone_other} onChange={(e) => setPhone("phone_other", e.target.value)} placeholder="+…" data-testid="ride-phone-other" />}
                <Input type="tel" className="min-w-0 flex-1" value={form.passenger_phone} onChange={(e) => setPhone("passenger_phone", e.target.value)} placeholder="6 11 22 33 44" data-testid="ride-passenger-phone" />
              </div>
              {phoneMissingCode && <p className="text-xs text-destructive" data-testid="ride-phone-missing">Choisissez l'indicatif pays.</p>}
              {phoneRequired && <p className="text-xs text-destructive" data-testid="ride-phone-required">Le passager paie : indiquez son téléphone.</p>}</div>
          </div>

          <div className="space-y-2 min-w-0" data-testid="ride-requests">
            <Label>Demandes spéciales</Label>
            {requestOptions.length === 0
              ? <p className="text-xs text-muted-foreground">Aucune demande : ajoutez-en dans Réglages → Demandes spéciales.</p>
              : (
                <div className="flex flex-wrap gap-1.5" role="group" aria-label="Demandes spéciales">
                  {requestOptions.map((o) => {
                    const on = checked(o.key);
                    return (
                      <button key={o.key} type="button" role="checkbox" aria-checked={on} onClick={() => toggleRequest(o)}
                        title={o.retired ? "Retirée des Réglages : gardée sur cette course" : undefined} data-testid={`ride-request-${o.key}`}
                        className={cn("inline-flex items-center gap-1 rounded-full border px-2.5 py-1.5 text-xs transition-colors max-w-full break-words text-left disabled:opacity-50 disabled:pointer-events-none",
                          on ? "border-sky-300 bg-sky-100 text-sky-800 dark:border-sky-800 dark:bg-sky-900/30 dark:text-sky-300" : "border-border text-muted-foreground hover:text-foreground hover:bg-muted")}>
                        {on && <Check className="h-3 w-3 shrink-0" aria-hidden="true" />}{o.label}
                      </button>
                    );
                  })}
                </div>
              )}
            {/* Précision facultative : jamais obligatoire, proposée seulement si la demande cochée a une indication. */}
            {form.special_requests.map((r) => {
              const hint = requestOptions.find((o) => o.key === r.key)?.detail_hint || "";
              if (detailOpen[r.key] || r.detail) {
                return (
                  <div key={r.key} className="flex items-center gap-2 min-w-0" data-testid={`ride-request-detail-row-${r.key}`}>
                    <span className="text-xs text-muted-foreground shrink-0 max-w-[45%] truncate">{r.label}</span>
                    <Input className="h-8 min-w-0 flex-1" value={r.detail} onChange={(e) => setDetail(r.key, e.target.value)} maxLength={120}
                      placeholder={hint || "Précision"} autoFocus={!!detailOpen[r.key] && !r.detail} data-testid={`ride-request-detail-${r.key}`} />
                  </div>
                );
              }
              if (!hint || locked) return null; // course attribuée (secrétaire) : pas de lien « + préciser »
              return (
                <button key={r.key} type="button" onClick={() => setDetailOpen((d) => ({ ...d, [r.key]: true }))}
                  className="block text-xs text-accent py-1.5 -my-1 text-left" data-testid={`ride-request-more-${r.key}`}>
                  {`+ préciser ${r.label} (${hint})`}
                </button>
              );
            })}
          </div>

          <div className="space-y-2 min-w-0">
            <Label>N° de vol ou de train</Label>
            <Input value={form.travel_ref} onChange={(e) => set("travel_ref", e.target.value)} placeholder="Ex. AF7700" data-testid="ride-travel-ref" />
            <FlightLinks travelRef={form.travel_ref} testid="ride-flight-links" />
          </div>

          {/* Classe demandée (lot T2) : remplie par le véhicule, choisissable sans véhicule (la secrétaire aussi). */}
          <div className="space-y-2 min-w-0"><Label>Classe de véhicule</Label>
            <Select value={form.vehicle_class || "none"} onValueChange={(v) => set("vehicle_class", v === "none" ? "" : v)}>
              <SelectTrigger className="min-w-0 [&>span]:truncate [&>span]:block" data-testid="ride-class"><SelectValue placeholder="Aucune" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucune</SelectItem>
                {form.vehicle_class && !classes.includes(form.vehicle_class) && <SelectItem value={form.vehicle_class}>{form.vehicle_class}</SelectItem>}
                {classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select></div>

          {limited ? (
            <p className="text-sm text-muted-foreground" data-testid="ride-dispatch-note">
              {mission?.employee_id
                ? [`${voc.Employee} : ${assigned?.name || "…"}`, selFleet && `Véhicule : ${fleetName(selFleet)}`].filter(Boolean).join(" · ")
                : `Le dispatch choisira le ${voc.employee} et le véhicule.`}
            </p>
          ) : <>
          <div className={grid2}>
            <div className="space-y-2 min-w-0"><Label>Véhicule disponible</Label>
              <Select value={form.fleet_vehicle_id || "none"} onValueChange={(v) => {
                const id = v === "none" ? "" : v;
                const classe = fleet.find((x) => x.id === id)?.vehicle_class;
                setForm((f) => ({ ...f, fleet_vehicle_id: id, ...(classe ? { vehicle_class: classe } : {}) })); // la classe suit le véhicule
              }}>
                <SelectTrigger className="min-w-0 [&>span]:truncate [&>span]:block" data-testid="ride-fleet"><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun</SelectItem>
                  {fleetMissing && <SelectItem value={form.fleet_vehicle_id}>{fleetName(selFleet) || "Véhicule"}{freeFleet ? " (indisponible)" : ""}</SelectItem>}
                  {(freeFleet || []).map((v) => <SelectItem key={v.id} value={v.id}>{fleetName(v)}{v.seats ? ` · ${v.seats} places` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
              {fleet.length === 0 && <p className="text-xs text-muted-foreground">Aucun véhicule : ajoutez-en dans la page Véhicules.</p>}</div>
            <div className="space-y-2 min-w-0"><Label>{`${voc.Employee} disponible`}</Label>
              <Select value={form.employee_id || "none"} onValueChange={(v) => set("employee_id", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="ride-employee"><SelectValue placeholder="Non assigné" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Non assigné</SelectItem>
                  {assignedMissing && <SelectItem value={assigned.id}>{assigned.name || voc.Employee}{available ? " (occupé)" : ""}</SelectItem>}
                  {(available || []).map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                  {unavailable.length > 0 && (
                    <SelectGroup data-testid="ride-employee-unavailable">
                      <SelectLabel className="text-xs font-normal text-muted-foreground">Indisponibles</SelectLabel>
                      {unavailable.map((e) => (
                        <SelectItem key={e.id} value={e.id} data-testid={`ride-employee-unav-${e.id}`}>
                          {`${e.name} — indisponible ${unavShort(e.unavailable[0], tz, form.start ? dayIn(new Date(form.start).toISOString(), tz) : "")}`}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select></div>
          </div>
          <p className="text-xs text-muted-foreground">{`Seuls les ${voc.employees} et les véhicules libres sur ce créneau apparaissent ; un ${voc.employee} indisponible demande une confirmation.`}</p>
          </>}
          </fieldset>

          {conflicts.length > 0 && (
            <div className="p-3 rounded-xl border border-destructive/40 bg-destructive/10 text-sm" data-testid="conflict-alert">
              <div className="flex items-center gap-2 font-medium text-destructive"><AlertTriangle className="h-4 w-4" /> Conflit d'horaire</div>
              {conflicts.map((c) => <div key={c.id} className="text-muted-foreground mt-1">{missionLabel(c)} · {fmtDateTime(c.start)} → {fmtDateTime(c.end)}</div>)}
            </div>
          )}

          {/* Course facturée (lot T6) : prix grisé, le serveur refuserait tout changement. */}
          {billed && <p className="text-sm rounded-xl border border-border bg-muted/50 p-3" data-testid="ride-billed-note">{`${voc.Mission} facturée (${mission.invoiced.join(", ")}) : prix verrouillé.`}</p>}
          {/* Liens du bloc (« Utiliser », « Ajouter », « Recalculer ») : gris quand le prix est verrouillé. */}
          <fieldset disabled={billed} className="min-w-0 [&_button:disabled]:text-muted-foreground [&_button:disabled]:cursor-not-allowed" data-testid="ride-price-fieldset">
          <RidePrice key={priceKey} draft={form} excludeId={mission?.id} price={form.price_ttc} details={mission?.price_details} tz={tz} cur="EUR"
            onChange={({ price, details }) => { setForm((f) => (String(f.price_ttc) === String(price) ? f : { ...f, price_ttc: price })); setPriceDetails(details); }} />
          </fieldset>
          {cancelled && (
            <div className="space-y-2" data-testid="ride-cancel-fee-block"><Label>Frais d'annulation (TTC)</Label>
              <Input type="number" min="0" step="0.01" value={form.cancellation_fee} disabled={billed} onChange={(e) => set("cancellation_fee", e.target.value)}
                placeholder="Aucun" data-testid="ride-cancel-fee" />
              <p className="text-xs text-muted-foreground">{`Avec des frais, la ${voc.mission} annulée apparaît dans « À facturer » (au taux de TVA de la ${voc.mission}, à faire confirmer par un comptable).`}</p></div>
          )}
          {/* Commission du concierge (lot T6) : une dépense de la Rentabilité, jamais sur la facture ni chez le chauffeur. */}
          <div className="space-y-2 min-w-0" data-testid="ride-commission-block"><Label>Commission du concierge</Label>
            <div className="flex flex-wrap gap-2 min-w-0">
              <Select value={form.com_mode} onValueChange={(v) => set("com_mode", v)}>
                <SelectTrigger className="w-32 shrink-0" data-testid="ride-commission-mode"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="pct">% du TTC</SelectItem><SelectItem value="amount">Montant €</SelectItem></SelectContent>
              </Select>
              <Input type="number" min="0" step="0.01" className="w-28 shrink-0" value={form.com_value} onChange={(e) => set("com_value", e.target.value)} placeholder="Aucune" data-testid="ride-commission-value" />
              <Input className="min-w-[10rem] flex-1" value={form.com_beneficiary} onChange={(e) => set("com_beneficiary", e.target.value)} placeholder="Bénéficiaire (facultatif)" maxLength={80} data-testid="ride-commission-beneficiary" />
            </div>
            <p className="text-xs text-muted-foreground">Comptée en dépense dans la Rentabilité, à la date de la course ; jamais sur la facture.</p></div>
          <div className="space-y-2"><Label>Notes / instructions</Label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Pancarte, accueil, consignes pour le chauffeur…" data-testid="ride-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Annuler</Button>
          <Button onClick={save} disabled={saving} data-testid="mission-save" className="rounded-full">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
